import { ControlType, Property, PropertyType } from "../../../base/types";
import { FC, useEffect, useState } from "react";
import { IControlAttributes, IPageLink } from "../../../base/models";
import { ServiceRequestInner, ServiceRequestProvider } from "./service-request";
import { useNavigate, useSearchParams } from "react-router-dom";

import { DesignerMode } from "../../types";
import { GatePassHistory } from "./service-request-actions/GatePassHistory";
import { PageLinkHelper } from "../../../base/helpers";
import { Registry } from "../../../base/register";
import { useAuth } from "../../../../modules/auth";
import { useGetActiveServiceRequest } from "./helpers";
import { useGetProperties } from "../../../base/hooks";

export const ServiceRequestComponent: FC<IControlAttributes> = ({
    control, className, children, props, designerMode, ...rest
}) => {
    const { properties } = useGetProperties({ bindings: control.bindings });
    const hidden: boolean | undefined = properties[Property.Hidden]
    const tooltip: string | undefined = properties[Property.Tooltip];
    const isEditable: boolean | undefined = properties[Property.ModifyAllowed];
    const navigateTo: IPageLink | undefined = properties[Property.PageLink];
    const relativePath: string | undefined = properties[Property.RelativePath];
    const entity: string | undefined = properties[Property.Entity];
    const serviceId2: string | undefined = properties[Property.ServiceId];
    const serviceRequestId2: string | undefined = properties[Property.ServiceRequestId];
    const hideHeader: boolean | undefined = properties[Property.HideHeader];
    const changeStatus: boolean | undefined = properties[Property.ChangeStatus];
    const editOnly: boolean | undefined = properties[Property.EditOnly];
    const documentsOnly: boolean | undefined = properties[Property.DocumentsOnly];
    const enableApproval: boolean | undefined = properties[Property.EnableApproval];
    const enableResend: boolean | undefined = properties[Property.EnableResend];
    const enableSubmit: boolean | undefined = properties[Property.EnableSubmit];
    const submitLabel: string | undefined = properties[Property.Label];
    const openActiveRequest: string | undefined = properties[Property.OpenActiveRequest];
    const navigateToOnCancel: IPageLink | undefined = properties[Property.CancelPageLink];
    const navigateToOnReOpen: IPageLink | undefined = properties[Property.ReOpenPageLink];
    const navigateToOnExtend: IPageLink | undefined = properties[Property.ExtendPageLink];
    const showHistory: boolean | undefined = properties[Property.ShowHistory];
    const hideTitle: boolean | undefined = properties[Property.HideTitle];
    const isSendingForApproval: boolean | undefined = properties[Property.IsSendingForApproval];

    const [serviceRequestId, setServiceRequestId] = useState<string>()
    const [serviceId, setServiceId] = useState<string>()
    const { getActiveRequestId } = useGetActiveServiceRequest()

    const [searchParams] = useSearchParams();
    const navigate = useNavigate()
    const { siteId } = useAuth()

    useEffect(() => {
        const queryServiceId = searchParams.get("serviceId") || undefined
        const queryRequestId = searchParams.get("id") || undefined

        const serviceId = serviceId2 || queryServiceId;
        const requestId = serviceRequestId2 || queryRequestId;

        setServiceRequestId(requestId)
        setServiceId(serviceId)
        // eslint-disable-next-line
    }, [properties])

    useEffect(() => {
        if (!openActiveRequest || !serviceId)
            return;

        getActiveRequestId(serviceId)
            .then(id => setServiceRequestId(id))
        // eslint-disable-next-line
    }, [openActiveRequest, serviceId])

    const onComplete = (requestId: string) => {
        if (siteId && navigateTo && navigateTo.pageId)
            navigate(PageLinkHelper.getPageLinkUrl(siteId, navigateTo))
        else
            setServiceRequestId(requestId)
    }

    const onCancel = () => {
        if (siteId && navigateToOnCancel && navigateToOnCancel.pageId)
            navigate(PageLinkHelper.getPageLinkUrl(siteId, navigateToOnCancel))
    }
    const onReOpen = () => {
        if (siteId && navigateToOnReOpen && navigateToOnReOpen.pageId)
            navigate(PageLinkHelper.getPageLinkUrl(siteId, navigateToOnReOpen))
    }
    const onExtended = () => {
        if (siteId && navigateToOnExtend && navigateToOnExtend.pageId)
            navigate(PageLinkHelper.getPageLinkUrl(siteId, navigateToOnExtend))
    }
    if (designerMode === DesignerMode.View && hidden === true) {
        return <></>
    }

    if (designerMode === DesignerMode.Draft) {
        return <div>
            <div className="alert alert-info fs-8 mb-0">
                <div className="badge badge-info mb-2">Service Request Component</div>
                <p className="mb-0">Click the preview button to check the layout</p>
            </div>
        </div>
    }

    return (
        <ServiceRequestProvider
            entity={entity}
            enableApprovalSMS={enableResend}
            serviceId={serviceId}
            requestId={serviceRequestId}>
            <ServiceRequestInner
                className={className}
                isEditable={isEditable}
                editOnly={editOnly}
                tooltip={tooltip}
                submitLabel={submitLabel}
                entity={entity}
                relativePath={relativePath}
                hideHeader={hideHeader}
                hideTitle={hideTitle}
                changeStatus={changeStatus}
                enableResend={enableResend}
                enableSubmit={enableSubmit}
                documentsOnly={documentsOnly}
                enableApproval={enableApproval}
                onComplete={onComplete}
                isSendingForApproval={isSendingForApproval}
                onCancel={onCancel}
                onExtended={onExtended}
                onReOpen={onReOpen} />

            <div className="d-flex">
                {(showHistory === true) && <GatePassHistory />}
                <div className="flex-fill" />
            </div>
        </ServiceRequestProvider>
    )
}

Registry.addControl({
    id: "775c4b16-9d53-4381-9984-6abbe3067f4b",
    name: "Service Request",
    type: ControlType.ServiceRequest,
    properties: [
        { id: Property.Entity, name: 'Service Request Entity', type: PropertyType.Entity },
        { id: Property.ServiceId, name: 'Service Id', type: PropertyType.String },
        { id: Property.ServiceRequestId, name: 'Service Request Id', type: PropertyType.String },
        { id: Property.RelativePath, name: 'Relative Path', type: PropertyType.String },
        { id: Property.ModifyAllowed, name: 'Edit Mode', type: PropertyType.Boolean },
        { id: Property.ChangeStatus, name: 'Change Status', type: PropertyType.Boolean },
        { id: Property.HideHeader, name: 'Hide Header', type: PropertyType.Boolean },
        { id: Property.PageLink, name: 'Navigate To', type: PropertyType.PageLink },
        { id: Property.Tooltip, name: 'Tooltip', type: PropertyType.String },
        { id: Property.Tooltip, name: 'Tooltip', type: PropertyType.String },
        { id: Property.EditOnly, name: 'Edit Only', type: PropertyType.Boolean },
        { id: Property.DocumentsOnly, name: 'Documents Only', type: PropertyType.Boolean },
        { id: Property.EnableApproval, name: 'Enable Approval', type: PropertyType.Boolean },
        { id: Property.EnableResend, name: 'Enable Resend', type: PropertyType.Boolean },
        { id: Property.Label, name: 'Submit Button Label', type: PropertyType.String },
        { id: Property.EnableSubmit, name: 'Enable Submit', type: PropertyType.Boolean },
        { id: Property.OpenActiveRequest, name: 'Open Active Request', type: PropertyType.Boolean },
        { id: Property.ShowHistory, name: 'Show History', type: PropertyType.Boolean },
        { id: Property.CancelPageLink, name: 'Navigate To on Cancel', type: PropertyType.PageLink },
        { id: Property.ReOpenPageLink, name: 'Navigate To on ReOpen', type: PropertyType.PageLink },
        { id: Property.ExtendPageLink, name: 'Navigate To on Extend', type: PropertyType.PageLink },
        { id: Property.HideTitle, name: 'Hide Service Title', type: PropertyType.Boolean },
    ],
    component: ServiceRequestComponent
})

